import React from 'react';
import { Link } from 'react-router-dom';
import { FeatureKey, PRICING_TIERS, FEATURE_ACCESS, SubscriptionPlan } from '../../types/subscription';
import { useSubscription } from '../../context/SubscriptionContext';

interface UpgradePromptProps {
  feature: FeatureKey;
  isOpen: boolean;
  onClose: () => void;
}

// Feature display info
const FEATURE_INFO: Record<FeatureKey, { title: string; description: string }> = {
  unlimited_recordings: {
    title: 'Unlimited Recordings',
    description: 'Record as many times as you want without daily limits',
  },
  ai_coach: {
    title: 'AI Coach Feedback',
    description: 'Get personalized coaching tips to improve your pronunciation',
  },
  phoneme_analysis: {
    title: 'Phoneme Analysis',
    description: 'See detailed scores for each sound you pronounce',
  },
  prosody_analysis: {
    title: 'Prosody & Intonation',
    description: 'Learn proper rhythm, stress patterns, and intonation',
  },
  pronunciation_tips: {
    title: 'Pronunciation Tips',
    description: 'Get actionable tips on how to improve specific sounds',
  },
  interview_practice: {
    title: 'Interview Practice',
    description: 'Prepare for job interviews with AI-generated questions',
  },
  interview_advanced_feedback: {
    title: 'Advanced Interview Feedback',
    description: 'Get detailed analysis and improvement suggestions for your answers',
  },
  interview_unlimited_questions: {
    title: 'Unlimited Questions',
    description: 'Access unlimited interview question sets across all industries',
  },
  ielts_practice: {
    title: 'IELTS Practice',
    description: 'Practice IELTS speaking, writing, and reading tests',
  },
  ielts_full_duration: {
    title: 'Full IELTS Duration',
    description: 'Access full-length IELTS exam simulations',
  },
  ielts_detailed_scoring: {
    title: 'Detailed Band Scoring',
    description: 'Get comprehensive band score breakdowns for each criteria',
  },
  ielts_personalized_tips: {
    title: 'Personalized IELTS Tips',
    description: 'Receive customized improvement suggestions based on your performance',
  },
};

// Get the minimum plan required for a feature
const getMinimumPlan = (feature: FeatureKey): SubscriptionPlan => {
  const plans: SubscriptionPlan[] = ['basic', 'pro'];
  for (const plan of plans) {
    if (FEATURE_ACCESS[plan].includes(feature)) {
      return plan;
    }
  }
  return 'pro'; // Default - Pro includes all features
};

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ feature, isOpen, onClose }) => {
  const { plan: currentPlan } = useSubscription();

  if (!isOpen) return null;

  const featureInfo = FEATURE_INFO[feature];
  const minimumPlan = getMinimumPlan(feature);
  const recommendedTier = PRICING_TIERS.find((t) => t.id === minimumPlan);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Lock Icon */}
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>

        {/* Content */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Unlock {featureInfo.title}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {featureInfo.description}
          </p>
        </div>

        {/* Recommended Plan */}
        {recommendedTier && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Recommended Plan</span>
              <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">{recommendedTier.name}</span>
            </div>
            <ul className="space-y-2">
              {recommendedTier.features.slice(0, 3).map((f, i) => (
                <li key={i} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                  <svg className="w-4 h-4 text-green-500 dark:text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Current Plan Info */}
        {currentPlan !== 'free' && (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            Your current plan: <span className="font-medium">{currentPlan}</span>
          </p>
        )}

        {/* CTA Buttons */}
        <div className="flex flex-col gap-3">
          <Link
            to="/pricing"
            onClick={onClose}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors text-center"
          >
            View Pricing Plans
          </Link>
          <button
            onClick={onClose}
            className="w-full py-3 px-4 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 font-medium transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
